import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, Settings, ShieldCheck, Menu, X, LogOut, ChevronDown, Search,
  Building2, KeyRound, Plus, UserPlus, Bell, PanelLeftClose, PanelLeft, Workflow, CheckSquare, ListChecks, UserCheck, FolderKanban, CalendarDays, CalendarClock, AlertTriangle, Briefcase, Mail, UserCircle, HelpCircle, GraduationCap, RefreshCw, NotebookPen, LifeBuoy, Headphones, PlayCircle, Lightbulb, Library, Newspaper, BarChart3,
} from "lucide-react";
import { Download, UsersRound, Handshake, Target, BriefcaseBusiness, Route as RouteIcon, Inbox, FileText, BookOpen, Send, Activity, CalendarPlus, MessageSquare } from "lucide-react";
import { loadUserTz } from "@/lib/tz";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { useAppUpdate } from "@/hooks/useAppUpdate";
import { TaskFormDialog } from "@/components/tasks/TaskFormDialog";
import { AddLeadDialog } from "@/components/recruiting/AddLeadDialog";
import { Logo, LogoMark } from "@/components/brand/Logo";
import { UserAvatar } from "@/components/common/UserAvatar";
import { DistrictSelector } from "@/components/layout/DistrictSelector";
import { ViewAsControl, ViewAsBanner } from "@/components/layout/ViewAs";
import { SupportStatusControl } from "@/components/support/SupportStatusControl";
import { SupportPresenceIndicator } from "@/components/support/SupportPresenceIndicator";
import { GlobalSearch } from "@/components/common/GlobalSearch";
import { useContentLabels } from "@/components/knowledge/Topics";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { InstallAppDialog } from "@/components/InstallPrompt";
import { useAdminCan } from "@/pages/admin/adminCards";

function NavItem({ to, icon: Icon, label, collapsed, end, testId }) {
  return (
    <NavLink to={to} end={end} data-testid={testId}
      className={({ isActive }) =>
        cn("group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          collapsed && "justify-center px-0",
          isActive ? "bg-white/10 text-white" : "text-sidebar-foreground hover:bg-white/5 hover:text-white")}>
      {({ isActive }) => (
        <>
          {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r bg-brand-orange" />}
          <Icon className={cn("w-[18px] h-[18px] shrink-0", isActive && "text-brand-cyan")} />
          {!collapsed && <span className="truncate">{label}</span>}
        </>
      )}
    </NavLink>
  );
}

function NavAction({ icon: Icon, label, collapsed, testId, onClick }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={cn("w-full group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold transition-colors text-white bg-brand/90 hover:bg-brand",
        collapsed && "justify-center px-0")}>
      <Icon className="w-[18px] h-[18px] shrink-0" />
      {!collapsed && <span className="truncate">{label}</span>}
    </button>
  );
}

function NavGroup({ title, collapsed, storageKey, children }) {
  const [open, setOpen] = useState(() => localStorage.getItem(storageKey) !== "0");
  if (collapsed) return <div className="space-y-1 pt-2 first:pt-0">{children}</div>;
  const toggle = () => { const n = !open; setOpen(n); localStorage.setItem(storageKey, n ? "1" : "0"); };
  return (
    <div className="pt-3 first:pt-0">
      <button onClick={toggle} data-testid={`navgroup-${title.toLowerCase()}`}
        className="w-full flex items-center justify-between px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-widest text-white/40 hover:text-white/70 transition-colors">
        <span>{title}</span><ChevronDown className={cn("w-3 h-3 transition-transform", !open && "-rotate-90")} />
      </button>
      {open && <div className="space-y-1">{children}</div>}
    </div>
  );
}


export function AppShell({ children }) {
  const { user, isAdmin, isSuper, isGroupAdmin, isManager, isUsersAdmin, isOutside, isReferralPartner, canRecruiting, canTasks, currentAccount, ctx, activeAccount, switchAccount, logout, roles, viewAs, isOfficeUser, canSchedule } = useAuth();
  const labels = useContentLabels();
  const { hasAdmin, can: adminCan } = useAdminCan();
  const canKC = adminCan.kc;
  const [collapsed, setCollapsed] = useState(false);
  const [briefOk, setBriefOk] = useState(false);
  useEffect(() => { loadUserTz(); }, [activeAccount]);
  useEffect(() => {
    if (isOutside || isReferralPartner) { setBriefOk(false); return; }
    api.get("/activity-report/access").then((r) => setBriefOk(!!r.data.allowed)).catch(() => setBriefOk(false));
  }, [activeAccount, ctx, isOutside, isReferralPartner]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const [quickTask, setQuickTask] = useState(false);
  const [addLead, setAddLead] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [isSupportStaff, setIsSupportStaff] = useState(false);
  const [sitePages, setSitePages] = useState([]);
  useEffect(() => { api.get("/pages/nav").then((r) => setSitePages(r.data.items || [])).catch(() => setSitePages([])); }, [activeAccount, viewAs?.id]);
  const pagesIn = (sec) => sitePages.filter((p) => p.nav_section === sec).map((p) => (
    <NavItem key={p.slug} to={`/pages/${p.slug}`} icon={FileText} label={p.title} collapsed={collapsed} testId={`nav-page-${p.slug}`} />));
  const [partnerLinked, setPartnerLinked] = useState(false);
  React.useEffect(() => {
    setPartnerLinked(false);
    if (!isOutside || viewAs) return;
    api.get("/partners/me").then(() => setPartnerLinked(true)).catch(() => setPartnerLinked(false));
  }, [isOutside, viewAs, activeAccount]);
  const navigate = useNavigate();
  const location = useLocation();
  React.useEffect(() => {
    // (Outside Recruiters now have their own Tasks / Projects / Calendar.)
  }, [isOutside, location.pathname, navigate]);

  React.useEffect(() => {
    let alive = true;
    setIsSupportStaff(false);
    if (isOutside || isReferralPartner) return;
    api.get("/support-tickets/meta").then((r) => { if (alive) setIsSupportStaff(!!r.data?.is_staff); }).catch(() => {});
    return () => { alive = false; };
  }, [activeAccount, viewAs?.role, isOutside, isReferralPartner]);

  React.useEffect(() => {
    if (!canTasks && !isReferralPartner && !isOfficeUser && !isOutside) return;
    let alive = true;
    const load = () => api.get(isOutside ? "/partners/notifications" : "/notifications").then((r) => alive && setNotifs(r.data.items || [])).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [canTasks, isReferralPartner, isOfficeUser, isOutside, activeAccount]);

  const primaryRole = [...roles][0];
  const isInternalStaff = isAdmin || isGroupAdmin || isManager || isSuper || ["internal_recruiter", "recruiting_support"].some((r) => roles.has(r));
  const isAgentOnly = (roles.has("agent") || roles.has("agent_staff")) && !isInternalStaff && !canRecruiting;
  const { updateAvailable, reload: reloadApp } = useAppUpdate();
  const canCreatePerson = isAdmin || isManager || roles.has("internal_recruiter");
  const canAddLead = isReferralPartner || isOutside || isSuper || isAdmin || isManager || isGroupAdmin || roles.has("internal_recruiter");
  const canWorkspace = canTasks || isReferralPartner || isOfficeUser;
  const roleLabel = ctx?.memberships?.find((m) => m.client_account_id === activeAccount)?.role_label
    || (isSuper ? "Super Admin" : "");

  const outsideNav = (
    <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto tr-scroll" data-testid="outside-nav">
      <NavGroup title="Home" collapsed={collapsed} storageKey="tr_nav_or_home">
        <NavItem to="/" end icon={LayoutDashboard} label="My Dashboard" collapsed={collapsed} testId="nav-home" />
        <NavItem to="/tasks" icon={CheckSquare} label="My Tasks" collapsed={collapsed} testId="nav-tasks" />
        <NavItem to="/projects" icon={FolderKanban} label="My Projects" collapsed={collapsed} testId="nav-projects" />
      </NavGroup>
      <NavGroup title="Recruiting" collapsed={collapsed} storageKey="tr_nav_or_recruiting">
        <NavItem to="/referrals" end icon={LayoutDashboard} label="My Recruiting Dashboard" collapsed={collapsed} testId="nav-referral-dashboard" />
        <NavItem to="/my-candidates" icon={Users} label="My Leads & Candidates" collapsed={collapsed} testId="nav-my-candidates" />
        <NavItem to="/referrals/invite" icon={Send} label="Invite a Candidate" collapsed={collapsed} testId="nav-rp-invite" />
        <NavItem to="/referrals/activity" icon={Activity} label="Recruiter Activity" collapsed={collapsed} testId="nav-rp-activity" />
        <NavItem to="/pathways-to-ownership" icon={RouteIcon} label="Pathways to Ownership" collapsed={collapsed} testId="nav-pathways" />
        {partnerLinked && <NavItem to="/partner" icon={Handshake} label="Recruiting Partner" collapsed={collapsed} testId="nav-partner" />}
      </NavGroup>
      <NavGroup title="Calendar & Meetings" collapsed={collapsed} storageKey="tr_nav_calendar">
        <NavItem to="/calendar" icon={CalendarDays} label="My Calendar" collapsed={collapsed} testId="nav-calendar" end />
        <NavItem to="/meetings/book" icon={CalendarClock} label="Book a Meeting" collapsed={collapsed} testId="nav-book-meeting" />
        <NavItem to="/referrals/meetings" icon={CalendarPlus} label="Document a Meeting" collapsed={collapsed} testId="nav-rp-meeting" />
        <NavItem to="/events" icon={GraduationCap} label="Training Calendar" collapsed={collapsed} testId="nav-events" end />
      </NavGroup>
      <NavGroup title="Training & Resources" collapsed={collapsed} storageKey="tr_nav_learning">
        <NavItem to="/training" icon={PlayCircle} label={labels.training} collapsed={collapsed} testId="nav-training" />
        <NavItem to="/referrals/knowledge" icon={BookOpen} label="Knowledge Center" collapsed={collapsed} testId="nav-rp-knowledge" />
      </NavGroup>
      <NavGroup title="Support" collapsed={collapsed} storageKey="tr_nav_rp_support">
        <NavItem to="/help" icon={LifeBuoy} label="Help Center" collapsed={collapsed} testId="nav-rp-help" />
        <NavItem to="/support/my-tickets" icon={MessageSquare} label="My Support Tickets" collapsed={collapsed} testId="nav-rp-tickets" />
      </NavGroup>
    </nav>
  );
  const nav = isOutside ? outsideNav : isReferralPartner ? (
    <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto tr-scroll">
      <NavItem to="/" end icon={LayoutDashboard} label="My Dashboard" collapsed={collapsed} testId="nav-home" />
      <NavGroup title="Recruiting" collapsed={collapsed} storageKey="tr_nav_referrals">
        <NavItem to="/referrals" end icon={LayoutDashboard} label="My Recruiting Dashboard" collapsed={collapsed} testId="nav-referral-dashboard" />
        <NavItem to="/referrals/list" icon={Users} label="My Candidates & Leads" collapsed={collapsed} testId="nav-my-referrals" />
        <NavItem to="/referrals/invite" icon={Send} label="Invite a Candidate" collapsed={collapsed} testId="nav-rp-invite" />
        <NavItem to="/referrals/activity" icon={Activity} label="Recruiter Activity" collapsed={collapsed} testId="nav-rp-activity" />
      </NavGroup>
      <NavGroup title="Learn" collapsed={collapsed} storageKey="tr_nav_rp_learn">
        <NavItem to="/pathways-to-ownership" icon={RouteIcon} label="Pathways to Ownership" collapsed={collapsed} testId="nav-pathways" />
      </NavGroup>
      <NavGroup title="Projects & Tasks" collapsed={collapsed} storageKey="tr_nav_work">
        <NavItem to="/tasks" icon={CheckSquare} label="My Tasks" collapsed={collapsed} testId="nav-tasks" />
        <NavItem to="/projects" icon={FolderKanban} label="My Projects" collapsed={collapsed} testId="nav-projects" />
      </NavGroup>
      <NavGroup title="Calendar & Meetings" collapsed={collapsed} storageKey="tr_nav_calendar">
        <NavItem to="/calendar" icon={CalendarDays} label="My Calendar" collapsed={collapsed} testId="nav-calendar" end />
        <NavItem to="/referrals/meetings" icon={CalendarPlus} label="Schedule / Document a Meeting" collapsed={collapsed} testId="nav-rp-meeting" />
      </NavGroup>
      <NavGroup title="Training & Resources" collapsed={collapsed} storageKey="tr_nav_learning">
        <NavItem to="/training" icon={PlayCircle} label={labels.training} collapsed={collapsed} testId="nav-training" />
        <NavItem to="/events" icon={GraduationCap} label="Training Calendar" collapsed={collapsed} testId="nav-events" end />
        <NavItem to="/referrals/knowledge" icon={BookOpen} label="Knowledge Center" collapsed={collapsed} testId="nav-rp-knowledge" />
        {pagesIn("agent_learning")}
      </NavGroup>
      <NavGroup title="Support" collapsed={collapsed} storageKey="tr_nav_rp_support">
        <NavItem to="/help" icon={LifeBuoy} label="Help Center" collapsed={collapsed} testId="nav-rp-help" />
        <NavItem to="/support/my-tickets" icon={MessageSquare} label="My Support Requests" collapsed={collapsed} testId="nav-rp-tickets" />
      </NavGroup>
    </nav>
  ) : (
    <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto tr-scroll">
      <NavGroup title="Home" collapsed={collapsed} storageKey="tr_nav_home">
        <NavItem to="/" end icon={LayoutDashboard} label="Dashboard" collapsed={collapsed} testId="nav-dashboard" />
        {canWorkspace && <NavItem to="/tasks" icon={CheckSquare} label="My Tasks" collapsed={collapsed} testId="nav-tasks" />}
        {canWorkspace && <NavItem to="/projects" icon={FolderKanban} label="My Projects" collapsed={collapsed} testId="nav-projects" />}
        {roles.has("agent") && <NavItem to="/my-proteges" icon={GraduationCap} label="My Protégés" collapsed={collapsed} testId="nav-my-proteges" />}
        {roles.has("agent") && <NavItem to="/my-reviews" icon={FileText} label="My Reviews" collapsed={collapsed} testId="nav-my-reviews" />}
      </NavGroup>
      {canRecruiting && (
        <NavGroup title="Recruiting" collapsed={collapsed} storageKey="tr_nav_recruiting">
          {isOutside && partnerLinked && <NavItem to="/partner" icon={Handshake} label="Recruiting Partner" collapsed={collapsed} testId="nav-partner" />}
          {isOutside && <NavItem to="/my-candidates" icon={Users} label="My Leads & Candidates" collapsed={collapsed} testId="nav-my-candidates" />}
          {(isSuper || isAdmin || isGroupAdmin || roles.has("external_recruiting_manager") || roles.has("external_recruiter_admin")) && <NavItem to="/pathways-to-ownership" icon={RouteIcon} label="Pathways to Ownership" collapsed={collapsed} testId="nav-pathways" />}
          {!isOutside && <NavItem to="/recruiting" icon={Workflow} label="Recruiting" collapsed={collapsed} testId="nav-recruiting" />}
          {!isOutside && canRecruiting && <NavItem to="/recruiting/new-leads" icon={Inbox} label="New Leads" collapsed={collapsed} testId="nav-new-leads" />}
          {!isOutside && (isAdmin || isGroupAdmin) && !isSuper && !isOfficeUser && <NavItem to="/agency/hiring" icon={BriefcaseBusiness} label="Agency Hiring" collapsed={collapsed} testId="nav-agency-hiring" />}
          {!isOutside && <NavItem to="/jobs" icon={Briefcase} label="Jobs & Intake" collapsed={collapsed} testId="nav-jobs" />}
          {!isOutside && <NavItem to="/communications" icon={Mail} label="Communications" collapsed={collapsed} testId="nav-communications" />}
          {pagesIn("recruiting")}
        </NavGroup>
      )}
      {!isOutside && (
        <NavGroup title="People & Agencies" collapsed={collapsed} storageKey="tr_nav_crm">
          <NavItem to="/people" icon={Users} label={isOfficeUser ? "My Contacts" : "People"} collapsed={collapsed} testId="nav-people" />
          {isOfficeUser && <NavItem to="/my-team" icon={UsersRound} label="My Team" collapsed={collapsed} testId="nav-my-team" />}
          {isOfficeUser && <NavItem to="/agency/leads" icon={Target} label="Leads" collapsed={collapsed} testId="nav-agency-leads" />}
          {isOfficeUser && <NavItem to="/agency/hiring" icon={BriefcaseBusiness} label="Hiring" collapsed={collapsed} testId="nav-agency-hiring" />}
          {isOfficeUser && <NavItem to="/my-roadmap" icon={RouteIcon} label="My Roadmap" collapsed={collapsed} testId="nav-my-roadmap" />}
          {canRecruiting && <NavItem to="/agencies" icon={Building2} label="Agencies" collapsed={collapsed} testId="nav-agencies" />}
          {(roles.has("agent") || roles.has("agent_staff") || isInternalStaff) && <NavItem to="/directory" icon={Users} label="Organization Directory" collapsed={collapsed} testId="nav-directory" />}
        </NavGroup>
      )}
      {!isOutside && (briefOk || isAdmin || isGroupAdmin || isSuper) && (
        <NavGroup title="Reports" collapsed={collapsed} storageKey="tr_nav_reports">
          {briefOk && <NavItem to="/activity-report" icon={BarChart3} label="Activity Report" collapsed={collapsed} testId="nav-activity-report" />}
          {(isAdmin || isGroupAdmin || isSuper) && <NavItem to="/reports/booker-activity" icon={BarChart3} label="Recruiter Activity" collapsed={collapsed} testId="nav-recruiter-activity" />}
        </NavGroup>
      )}
      <NavGroup title="Calendar & Meetings" collapsed={collapsed} storageKey="tr_nav_calendar">
        {!isOutside && <NavItem to="/calendar" icon={CalendarDays} label="My Calendar" collapsed={collapsed} testId="nav-calendar" end />}
        {isOfficeUser && canSchedule && <NavItem to="/calendar/scheduling-pages" icon={CalendarClock} label="Scheduling" collapsed={collapsed} testId="nav-scheduling" />}
        {canRecruiting && <NavItem to="/meetings/book" icon={CalendarClock} label="Book a Meeting" collapsed={collapsed} testId="nav-book-meeting" />}
        {canRecruiting && !isOutside && <NavItem to="/calendar/document-meeting" icon={NotebookPen} label="Document a Meeting" collapsed={collapsed} testId="nav-document-meeting" />}
        <NavItem to="/events" icon={GraduationCap} label="Training Calendar" collapsed={collapsed} testId="nav-events" end />
      </NavGroup>
      <NavGroup title="Training & Resources" collapsed={collapsed} storageKey="tr_nav_learning">
        <NavItem to="/training" icon={PlayCircle} label={labels.training} collapsed={collapsed} testId="nav-training" />
        {(roles.has("agent") || isInternalStaff) && <NavItem to="/idea-share" icon={Lightbulb} label="Agent Idea Share" collapsed={collapsed} testId="nav-idea-share" />}
        {isAgentOnly && <NavItem to="/library" icon={Library} label="Archive & Resources" collapsed={collapsed} testId="nav-library" />}
        {isInternalStaff && !viewAs && <NavItem to="/agent-dashboard" icon={Newspaper} label="Agent Portal" collapsed={collapsed} testId="nav-agent-dashboard" />}
        {pagesIn("agent_learning")}
      </NavGroup>
      {(hasAdmin || canKC || isUsersAdmin) && !isOfficeUser && (
        <NavGroup title="Administration" collapsed={collapsed} storageKey="tr_nav_admin">
          {(isUsersAdmin || isManager || isSuper) && <NavItem to="/admin/users" icon={UserCheck} label="Users" collapsed={collapsed} testId="nav-users" />}
          {canKC && <NavItem to="/admin/knowledge" icon={BookOpen} label="Knowledge Center" collapsed={collapsed} testId="nav-knowledge-center" />}
          {hasAdmin && <NavItem to="/admin/center" icon={Settings} label="Admin Center" collapsed={collapsed} testId="nav-admin" />}
        </NavGroup>
      )}
      <NavGroup title="Support" collapsed={collapsed} storageKey="tr_nav_support">
        <NavItem to="/help" icon={HelpCircle} label={labels.help} collapsed={collapsed} testId="nav-help-training" />
        <NavItem to="/support/my-tickets" icon={LifeBuoy} label="My Support Tickets" collapsed={collapsed} testId="nav-my-tickets" />
        {isSupportStaff && <NavItem to="/support/tickets" icon={Headphones} label="Support Queue" collapsed={collapsed} testId="nav-support-tickets" />}
        {pagesIn("support")}
      </NavGroup>
    </nav>
  );

  const bottom = (
    <div className="px-3 py-3 border-t border-white/10 space-y-1">
      {!collapsed && (
        <div className="mt-1 flex items-center gap-2.5 rounded-lg px-2 py-2 bg-white/5">
          <button onClick={() => navigate("/profile")} data-testid="sidebar-profile-card"
            className="flex items-center gap-2.5 min-w-0 flex-1 text-left hover:opacity-90 transition">
            <UserAvatar name={user?.name} size={32} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-[11px] text-white/50 truncate">{roleLabel}</p>
            </div>
          </button>
          <button onClick={logout} data-testid="sidebar-logout" className="text-white/50 hover:text-white"><LogOut className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );

  return (
    <TooltipProvider delayDuration={200}>
    <div className="min-h-screen bg-background">
      {/* Sidebar (desktop) */}
      <aside className={cn("fixed inset-y-0 left-0 z-40 hidden md:flex flex-col bg-sidebar transition-all duration-200",
        collapsed ? "w-[68px]" : "w-64")}>
        <div className={cn("flex items-center h-16 border-b border-white/10", collapsed ? "justify-center" : "px-5 justify-between")}>
          {collapsed ? <LogoMark size={34} /> : <Logo theme="dark" size={26} />}
        </div>
        {nav}
        {bottom}
        <button onClick={() => setCollapsed((c) => !c)} data-testid="sidebar-collapse"
          className="absolute -right-3 top-20 hidden md:flex items-center justify-center w-6 h-6 rounded-full bg-white border border-slate-200 shadow-sm text-brand hover:bg-slate-50">
          {collapsed ? <PanelLeft className="w-3.5 h-3.5" /> : <PanelLeftClose className="w-3.5 h-3.5" />}
        </button>
      </aside>

      {/* Sidebar (mobile) */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-slate-900/60" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-sidebar flex flex-col">
            <div className="flex items-center justify-between h-16 px-5 border-b border-white/10">
              <Logo theme="dark" size={26} />
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-nav-close"><X className="w-5 h-5 text-white" /></button>
            </div>
            <div onClick={() => setMobileOpen(false)} className="flex-1 flex flex-col">{nav}{bottom}</div>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className={cn("min-w-0 max-w-full transition-all duration-200", collapsed ? "md:pl-[68px]" : "md:pl-64")}>
        <ViewAsBanner />
        <header className="sticky top-0 z-30 flex h-16 min-w-0 items-center gap-3 border-b border-border bg-white/90 backdrop-blur-md px-4 sm:px-6">
          <button className="md:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-nav-open">
            <Menu className="w-5 h-5 text-slate-600" />
          </button>

          {!isOutside && !isReferralPartner && <GlobalSearch />}

          <div className="ml-auto flex min-w-0 shrink-0 items-center gap-1 sm:gap-1.5 2xl:gap-2">
            <ViewAsControl />
            {(canAddLead || canWorkspace || canCreatePerson) && <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" data-testid="quick-create" aria-label="Quick Create" title="Quick Create" className="bg-brand hover:bg-brand-strong text-white hidden sm:inline-flex">
                  <Plus className="w-4 h-4 2xl:mr-1.5" /><span className="hidden 2xl:inline">Quick Create</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white w-48">
                {canAddLead && <DropdownMenuItem data-testid="quick-create-lead" onClick={() => setAddLead(true)}><UserPlus className="w-4 h-4 mr-2" />Add Lead</DropdownMenuItem>}
                {canCreatePerson && (isAdmin || isManager || isGroupAdmin) && <DropdownMenuItem data-testid="quick-create-person" onClick={() => navigate("/people?create=1")}><Users className="w-4 h-4 mr-2" />Add Person</DropdownMenuItem>}
                {canWorkspace && <DropdownMenuItem data-testid="quick-create-task" onClick={() => setQuickTask(true)}><CheckSquare className="w-4 h-4 mr-2" />Task</DropdownMenuItem>}
                {canWorkspace && <DropdownMenuItem data-testid="quick-create-project" onClick={() => navigate("/projects?create=1")}><FolderKanban className="w-4 h-4 mr-2" />Project</DropdownMenuItem>}
                {(isAdmin || isManager || isGroupAdmin || isSuper) && <DropdownMenuItem data-testid="quick-create-event" onClick={() => navigate("/events/manage?create=1")}><GraduationCap className="w-4 h-4 mr-2" />Event</DropdownMenuItem>}
                {(isAdmin || isManager || isGroupAdmin || isSuper || isSupportStaff || roles.has("recruiting_support")) && !isOfficeUser && <DropdownMenuItem data-testid="quick-create-faq" onClick={() => navigate("/help?add_faq=1")}><HelpCircle className="w-4 h-4 mr-2" />Add FAQ Article</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>}

            <button data-testid="help-launcher" onClick={() => navigate("/help")} className="hidden sm:inline-flex p-2 rounded-lg hover:bg-secondary text-slate-500" aria-label="Help Center">
              <HelpCircle className="w-[18px] h-[18px]" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="notifications" className="relative p-2 rounded-lg hover:bg-secondary text-slate-500">
                  <Bell className="w-[18px] h-[18px]" />
                  {(notifs.length + (updateAvailable ? 1 : 0)) > 0 && <span data-testid="notif-count" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">{notifs.length + (updateAvailable ? 1 : 0)}</span>}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white w-80">
                {updateAvailable && (
                  <>
                    <DropdownMenuItem data-testid="app-update-notif" onSelect={(e) => { e.preventDefault(); reloadApp(); }}
                      className="flex items-start gap-2 bg-sky-50 focus:bg-sky-100 cursor-pointer">
                      <RefreshCw className="w-4 h-4 mt-0.5 shrink-0 text-[#006090]" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800">A new version is available</p>
                        <p className="text-xs text-slate-500">Click to refresh and get the latest updates.</p>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                {notifs.filter((n) => n.kind === "approval").length > 0 && (
                  <>
                    <DropdownMenuLabel className="text-xs" data-testid="notif-approval-label">Approval Needed</DropdownMenuLabel>
                    {notifs.filter((n) => n.kind === "approval").slice(0, 8).map((n) => (
                      <DropdownMenuItem key={n.key} data-testid={`notif-approval-${n.id}`}
                        onClick={() => navigate(n.link || "/admin/transfer-requests")}
                        className="flex items-start gap-2 bg-amber-50/60 focus:bg-amber-100">
                        <UserCheck className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-800 truncate">{n.title}</p>
                          {n.body && <p className="text-xs text-slate-500 line-clamp-2">{n.body}</p>}
                          <button data-testid={`notif-approval-review-${n.id}`}
                            onClick={(e) => { e.stopPropagation(); navigate(n.link || "/admin/transfer-requests"); }}
                            className="mt-1 text-xs font-semibold text-[#006090] hover:underline">Review Request →</button>
                        </div>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuLabel className="text-xs">{isOutside ? "Notifications" : "Reminders"}</DropdownMenuLabel>
                {notifs.filter((n) => n.kind !== "approval").length === 0 && !updateAvailable && <div className="px-3 py-6 text-center text-sm text-slate-400" data-testid="notif-empty">You're all caught up.</div>}
                {notifs.filter((n) => n.kind !== "approval").slice(0, 8).map((n) => (
                  <DropdownMenuItem key={n.key} data-testid={`notif-${n.task_id || n.key}`} onClick={() => { api.post(isOutside ? "/partners/notifications/read" : "/notifications/read", { key: n.key }).catch(() => {}); navigate(n.link || (n.task_id ? `/tasks/detail/${n.task_id}` : "/")); }}
                    className="flex items-start gap-2">
                    <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${n.kind === "overdue" ? "text-rose-500" : "text-amber-500"}`} />
                    <div className="min-w-0"><p className="text-sm text-slate-700 truncate">{n.title}</p>
                      <p className="text-xs text-slate-400">{n.kind === "overdue" ? `Overdue ${n.days_overdue}d` : n.kind === "due_today" ? "Due today" : (n.body || "")}</p></div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {!isOutside && !isReferralPartner && <DistrictSelector />}
            {!isOutside && !isReferralPartner && <div className="hidden sm:block"><SupportPresenceIndicator /></div>}
            <SupportStatusControl />
            {(canTasks || isReferralPartner) && <TaskFormDialog open={quickTask} onClose={() => setQuickTask(false)} onCreated={() => setQuickTask(false)} />}
            {canAddLead && <AddLeadDialog open={addLead} onClose={() => setAddLead(false)} onCreated={() => setAddLead(false)} />}

            {ctx?.accounts?.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="account-switcher" aria-label={`Account: ${currentAccount?.account_name || "Select account"}`} title={currentAccount?.account_name}
                    className="flex items-center gap-2 rounded-lg border border-border bg-white px-2.5 py-1.5 text-sm hover:bg-secondary/60 max-w-[220px]">
                    <span className="w-6 h-6 rounded-md bg-brand/10 flex items-center justify-center shrink-0"><Building2 className="w-3.5 h-3.5 text-brand" /></span>
                    <span className="hidden min-[1700px]:block truncate font-medium text-slate-700">{currentAccount?.account_name || "Select account"}</span>
                    <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white w-64">
                  <DropdownMenuLabel className="text-xs text-muted-foreground">{isSuper ? "Platform accounts" : "Your accounts"}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {ctx.accounts.map((a) => (
                    <DropdownMenuItem key={a.id} data-testid={`account-option-${a.id}`}
                      onClick={() => { switchAccount(a.id); window.location.reload(); }}
                      className="flex items-center justify-between gap-2">
                      <span className={cn("truncate", a.id === activeAccount && "font-semibold text-brand")}>{a.account_name}</span>
                      {a.status !== "active" && <StatusBadge status={a.status} />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="rounded-full ring-2 ring-transparent hover:ring-brand/20 transition"><UserAvatar name={user?.name} size={34} /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white w-56">
                <DropdownMenuLabel className="flex flex-col">
                  <span className="font-semibold text-slate-800">{user?.name}</span>
                  <span className="text-xs font-normal text-slate-500 truncate">{user?.email}</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5 text-xs text-slate-500 flex items-center gap-2">
                  <ShieldCheck className={cn("w-4 h-4", user?.mfa_enabled ? "text-success" : "text-warning")} />
                  MFA {user?.mfa_enabled ? "enabled" : (user?.mfa_required ? "required" : "off")}
                </div>
                <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate("/profile")}>
                  <UserCircle className="w-4 h-4 mr-2" /> My Profile
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-security" onClick={() => navigate("/security")}>
                  <KeyRound className="w-4 h-4 mr-2" /> Security & MFA
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-help" onClick={() => navigate("/help")}>
                  <HelpCircle className="w-4 h-4 mr-2" /> Help Center
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-install-app" onClick={() => setInstallOpen(true)}>
                  <Download className="w-4 h-4 mr-2" /> Install App
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-rose-600">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="min-w-0 px-4 sm:px-6 lg:px-8 py-6 max-w-[1400px] mx-auto tr-animate-in">{children}</main>
        <InstallAppDialog open={installOpen} onClose={() => setInstallOpen(false)} />
      </div>
    </div>
    </TooltipProvider>
  );
}
